"use client";

import { type FC, type Ref, type ReactNode, useEffect, useRef } from "react";
import {
  isDocumentDefined,
  isWindowDefined,
  mergeClasses,
} from "@huub-web/utils";
import { Flex } from "@huub-web/ui";

import { mergeRefs } from "@huub-web/ui/utils/mergeRefs";
import { useIsomorphicLayoutEffect } from "@huub-web/ui/hooks/useIsomorphicLayoutEffect";
import { usePubSub } from "@huub-web/ui/hooks/usePubSub";
import { useWindowSize } from "@huub-web/ui/hooks/useWindowSize";
import type { ChildrenProp, ClassNameProp } from "@huub-web/ui";
import { nextFrame } from "@huub-web/ui/utils";
import { LAYOUT_CONTENT_SCROLL_EMIT_TAG } from "../../config/layoutConsts";

import {
  documentBodyStyle,
  containerStyle,
  contentStyle,
} from "./AppLayoutContent.css";

export { LAYOUT_CONTENT_SCROLL_EMIT_TAG };

export type AppLayoutContentProps = ClassNameProp &
  ChildrenProp & {
    /**
     * Ref forwarded to the outer scroll container div
     */
    ref?: Ref<HTMLDivElement>;
    hasTaskBar?: boolean;
    leading?: ReactNode;
    trailing?: ReactNode;
  };

/**
 * The AppLayoutContent component.
 */
export const AppLayoutContent: FC<AppLayoutContentProps> = ({
  leading,
  hasTaskBar = false,
  className,
  children,
  trailing,
  ref,
  ...props
}) => {
  const windowSize = useWindowSize();
  const pubSub = usePubSub();

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!scrollRef.current || !contentRef.current) {
      return;
    }

    scrollRef.current.style.setProperty(
      "--content-width",
      `${contentRef.current.offsetWidth}px`,
    );
  }, [windowSize, hasTaskBar]);

  useEffect(() => {
    if (!isDocumentDefined() || !isWindowDefined()) {
      return;
    }

    // Defined inside the effect so the reference used for add/remove is the
    // same instance, independent of component-body render cycles or compiler
    // memoization behavior.
    const handleBodyScroll = () => {
      pubSub.publish("onScroll", {
        scrollTop: window.scrollY,
        tag: LAYOUT_CONTENT_SCROLL_EMIT_TAG,
      });
    };

    document.body.classList.add(documentBodyStyle);
    window.addEventListener("scroll", handleBodyScroll);

    return () => {
      document.body.classList.remove(documentBodyStyle);
      window.removeEventListener("scroll", handleBodyScroll);
    };
  }, [pubSub]);

  useEffect(() => {
    if (!isDocumentDefined() || !isWindowDefined()) {
      return;
    }

    const unsubscribeScrollToBottom = pubSub.subscribe(
      "scrollToBottom",
      ({ tag, instant }) => {
        if (tag === LAYOUT_CONTENT_SCROLL_EMIT_TAG) {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: instant ? "instant" : "smooth",
          });
        }
      },
    );

    const unsubscribeScrollToTop = pubSub.subscribe(
      "scrollToTop",
      ({ tag }) => {
        if (tag === LAYOUT_CONTENT_SCROLL_EMIT_TAG) {
          nextFrame(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          });
        }
      },
    );

    return () => {
      unsubscribeScrollToBottom();
      unsubscribeScrollToTop();
    };
  }, [pubSub]);

  return (
    <div
      ref={mergeRefs(scrollRef, ref)}
      className={mergeClasses(containerStyle, className)}
      {...props}
    >
      {leading}
      <Flex
        ref={contentRef}
        className={mergeClasses(contentStyle, "layout-content")}
        direction="column"
      >
        {children}
      </Flex>
      {trailing}
    </div>
  );
};
